import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import FileUpload from '@/components/FileUpload';
import ContentPreviewCard from '@/components/ContentPreviewCard';
import { WORKFLOW_STATUS_META } from '@/lib/statusColors';
import { kindLabel } from '@/lib/workflowKinds';

const STATUS_CLS = (s) => (WORKFLOW_STATUS_META[s]?.cls) || 'bg-slate-100 text-slate-600 border-slate-200';
const STATUS_LABEL = (s) => (WORKFLOW_STATUS_META[s]?.label) || (s || '').replace('_', ' ');

function fmtDate(d) { return d ? new Date(d).toLocaleString() : '—'; }

/* eslint-disable -- v6 react-hooks/set-state-in-effect false-positive on the fetch effect. */
export default function Approvals() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState(null);
  const [filter, setFilter] = useState('all');
  const [note, setNote] = useState('');

  const load = async () => {
    try {
      const { data } = await api.get('/workflows/', { params: filter === 'all' ? {} : { status: filter } });
      setItems(data);
      if (id) {
        const found = data.find((r) => r.request_id === id);
        if (found) setActive(found);
        else {
          // fetch directly
          const r = await api.get(`/workflows/${id}`);
          setActive(r.data);
        }
      } else if (data.length > 0) {
        // Always re-anchor the detail panel to the first row of the current
        // filter — prevents a stale "approved" item from staying open while
        // the user is filtering by "rejected".
        const stillVisible = active && data.some((r) => r.request_id === active.request_id);
        if (!stillVisible) setActive(data[0]);
      } else {
        setActive(null);
      }
    } catch { toast.error('Could not load approvals'); }
  };
  useEffect(() => { load(); }, [filter, id]);

  const decide = async (decision) => {
    if (!active) return;
    try {
      const { data } = await api.post('/workflows/decision', { request_id: active.request_id, decision, note });
      toast.success(decision === 'approve' ? 'Approved.' : 'Rejected.');
      setNote('');
      setActive(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const execute = async () => {
    if (!active) return;
    try {
      const { data } = await api.post(`/workflows/${active.request_id}/execute`);
      toast.success('Marked as executed.');
      setActive(data);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="ve-eyebrow">Approval Center</div>
          <h1 className="display text-3xl md:text-4xl mt-2 font-bold">Every change. Documented. Approved.</h1>
          <p className="text-sm text-slate-600 mt-2 max-w-2xl">Legal · CFO/CEO · Chairman. Customisable workflows per request type. Full audit trail.</p>
        </div>
        <div className="flex gap-2 self-start">
          {['all', 'in_review', 'approved', 'rejected', 'executed'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-bold rounded-md border transition-colors ${filter === f ? 'bg-[#0A0A0A] text-[#8B3FC5] border-[#0A0A0A]' : 'bg-white text-slate-700 border-slate-200 hover:border-[#8B3FC5]'}`}>
              {f.replace('_', ' ')}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-7 grid grid-cols-1 lg:grid-cols-12 gap-5">
        <aside className="lg:col-span-4 space-y-2 max-h-[640px] overflow-y-auto pr-1">
          {items.length === 0 && <div className="ve-card p-8 text-center text-sm text-slate-500">No requests in this filter.</div>}
          {items.map((r) => (
            <button key={r.request_id} onClick={() => { setActive(r); nav(`/dashboard/approvals/${r.request_id}`); }}
              data-testid={`wf-item-${r.request_id}`}
              className={`w-full text-left ve-card p-4 transition-all ${active?.request_id === r.request_id ? 'border-[#8B3FC5] ring-2 ring-[#8B3FC5]/20' : 'hover:border-[#8B3FC5]'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{kindLabel(r.kind)}</span>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded font-bold border ${STATUS_CLS(r.status)}`}>{STATUS_LABEL(r.status)}</span>
              </div>
              <h3 className="font-bold mt-1 text-sm">{r.title}</h3>
              <div className="mt-2 flex items-center gap-1">
                {(r.workflow_steps || []).map((s, i) => (
                  <div key={i} className={`flex-1 h-1 rounded-full ${
                    s.status === 'approved' ? 'bg-[#8B3FC5]' :
                    s.status === 'rejected' ? 'bg-rose-400' :
                    'bg-slate-200'
                  }`}/>
                ))}
              </div>
            </button>
          ))}
        </aside>

        <main className="lg:col-span-8">
          {!active ? (
            <div className="ve-card p-10 text-center text-sm text-slate-500">Select a request to review.</div>
          ) : (
            <div className="ve-card p-6">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">{kindLabel(active.kind)}</div>
                  <h2 className="display text-2xl font-bold mt-1">{active.title}</h2>
                  <p className="text-sm text-slate-500 mt-1">Requested {fmtDate(active.created_at)}</p>
                </div>
                <span className={`text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded font-bold border ${STATUS_CLS(active.status)}`}>{STATUS_LABEL(active.status)}</span>
              </div>

              <p className="mt-5 text-sm whitespace-pre-wrap leading-relaxed">{active.summary}</p>
              {active.entity_id && (
                <Link to={`/dashboard/entities-os/${active.entity_id}`} className="ve-btn-ghost mt-3 text-xs">View entity →</Link>
              )}

              {/* Native content preview — only for Content Studio submissions */}
              {active.kind === 'content_publish' && active.payload && (
                <div className="mt-6 border-t border-slate-200 pt-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
                    Content preview — exactly what will publish
                  </div>
                  <ContentPreviewCard payload={active.payload}/>
                  {active.payload.notes && (
                    <div className="mt-3 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-3"
                         data-testid="content-internal-notes">
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-slate-500 mb-1">
                        Internal notes from submitter
                      </div>
                      <div className="whitespace-pre-wrap">{active.payload.notes}</div>
                    </div>
                  )}
                </div>
              )}

              {/* Workflow progress */}
              <div className="mt-7">
                <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">Approval workflow</div>
                <div className="space-y-2">
                  {(active.workflow_steps || []).map((s, i) => {
                    const isCurrent = i === active.current_step && active.status === 'in_review';
                    return (
                      <div key={i} className={`flex items-center gap-3 p-3 rounded-md border ${
                        s.status === 'approved' ? 'bg-emerald-50 border-emerald-200' :
                        s.status === 'rejected' ? 'bg-rose-50 border-rose-200' :
                        isCurrent ? 'bg-amber-50 border-amber-300' :
                        'bg-white border-slate-200'
                      }`}>
                        <div className={`h-7 w-7 rounded-full flex items-center justify-center ${
                          s.status === 'approved' ? 'bg-emerald-500 text-white' :
                          s.status === 'rejected' ? 'bg-rose-500 text-white' :
                          isCurrent ? 'bg-amber-400 text-white' :
                          'bg-slate-200 text-slate-500'
                        }`}>
                          {s.status === 'approved' ? <CheckCircle2 size={14}/> :
                           s.status === 'rejected' ? <XCircle size={14}/> :
                           isCurrent ? <Clock size={14}/> :
                           <span className="text-[11px] font-bold">{i + 1}</span>}
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-bold">{s.label}</div>
                          <div className="text-xs text-slate-500 capitalize">{s.role}</div>
                          {s.decided_at && <div className="text-xs text-slate-500 mt-0.5">Decided {fmtDate(s.decided_at)}{s.note ? ` — “${s.note}”` : ''}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Attachments */}
              <div className="mt-7">
                <FileUpload entityId={active.entity_id || undefined} kind="other" label="Supporting documents" testid="approval-files"/>
              </div>

              {/* Decision controls — approvers for current step or holding_admin can act */}
              {(() => {
                const APPROVER_ROLES_MAP = {
                  legal: ['legal', 'holding_admin', 'system_admin'],
                  cfo: ['cfo', 'ceo', 'holding_admin', 'system_admin'],
                  ceo: ['ceo', 'cfo', 'holding_admin', 'system_admin'],
                  chairman: ['chairman', 'holding_admin', 'system_admin'],
                  marketing: ['marketing', 'comms_lead', 'ceo', 'holding_admin', 'system_admin'],
                  any_admin: ['holding_admin', 'system_admin'],
                };
                const curStep = (active.workflow_steps || [])[active.current_step];
                const allowedRoles = APPROVER_ROLES_MAP[curStep?.role] || ['holding_admin'];
                const canApprove = active.status === 'in_review' && user && allowedRoles.includes(user.role);
                const canExecute = active.status === 'approved' && user?.role === 'holding_admin';
                return (
                  <>
                    {canApprove && (
                      <div className="mt-6 border-t border-slate-200 pt-5">
                        <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-2">
                          Decision note (optional) — acting as <span className="text-[#5E1F8B]">{user.role}</span> for step &ldquo;{curStep?.label}&rdquo;
                        </div>
                        <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:border-[#8B3FC5] focus:ring-2 focus:ring-[#8B3FC5]/20"/>
                        <div className="mt-3 flex gap-2">
                          <button onClick={() => decide('approve')} data-testid="approve-btn" className="ve-btn-primary text-xs px-4 py-2"><CheckCircle2 size={12}/> Approve step</button>
                          <button onClick={() => decide('reject')} data-testid="reject-btn" className="ve-btn-secondary text-xs px-4 py-2"><XCircle size={12}/> Reject</button>
                        </div>
                      </div>
                    )}
                    {canExecute && (
                      <div className="mt-6 border-t border-slate-200 pt-5">
                        <button onClick={execute} data-testid="execute-btn" className="ve-btn-primary text-xs px-4 py-2"><ShieldCheck size={12}/> Mark as executed</button>
                      </div>
                    )}
                    {active.status === 'in_review' && !canApprove && (
                      <div className="mt-6 border-t border-slate-200 pt-5 text-xs text-slate-500">
                        Current step requires <span className="font-bold text-[#5E1F8B]">{(allowedRoles.join(' / '))}</span>. You ({user?.role}) cannot decide on this step.
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
